import { useTranslation } from '@fluxora/i18n';
import { Badge, Card, CardBody } from '@fluxora/ui';

import { presentableDisclosureText } from '../services/disclosureText.service';

/**
 * AC2 — EFL / TOS / YRAC disclosure, gated by the EFL publish flag
 * (`VITE_FEATURE_EFL_PUBLISH`, default false). Presenting a document as a live,
 * publishable PUCT §25.475 EFL is a compliance act; the seeded catalogue's EFL
 * is a DEV PLACEHOLDER (generation-mix figures are synthetic, TOS/YRAC are
 * placeholder text), so until the compliance checklist in the epic doc is
 * satisfied the flag stays false and the "Official EFL" badge is withheld.
 *
 * ⚠️ It used to ALSO show a "PREVIEW — not an official document" badge in that
 * state; that was removed on request (2026-09-18). The honesty guarantee now
 * rests entirely on the text gate below — placeholder prose is never rendered,
 * it is replaced by the with-your-contract line — so do not weaken that.
 *
 * `publishEnabled` is passed in (not read from env here) so the gate is trivially
 * testable in both positions.
 */
export interface EflDisclosureProps {
  readonly eflText: string;
  readonly tosText: string;
  readonly yracText: string;
  readonly publishEnabled: boolean;
}

export function EflDisclosure({
  eflText,
  tosText,
  yracText,
  publishEnabled,
}: EflDisclosureProps): JSX.Element {
  const { t } = useTranslation();

  /* Only the GENUINE catalogue prose renders — sentences that talk about
     placeholders are scaffolding, not content (see disclosureText.service).
     A field with nothing genuine left falls back to the true statement of
     where the full document lives. Nothing is ever invented here: a §25.475
     document cannot be fabricated, and the PREVIEW badge stays until the
     publish flag says the real one is wired. */
  const efl = presentableDisclosureText(eflText);
  const tos = presentableDisclosureText(tosText);
  const yrac = presentableDisclosureText(yracText);
  const withContract = t('shop.efl.docs.withContract');

  return (
    <Card variant="glass" padding="md" className="flex flex-col gap-3" data-testid="efl-disclosure">
      <CardBody className="flex flex-col gap-3 p-0">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-foreground">{t('shop.efl.title')}</h3>
          {/* The "PREVIEW — not an official document" badge that used to render
              here when `publishEnabled` was false was removed on request
              (2026-09-18). Nothing about the DOCUMENTS changed: the gate below
              still refuses to invent §25.475 prose, and a field with nothing
              genuine in it still falls back to "provided in full with your
              contract before you sign" rather than showing placeholder text.
              Only the badge is gone. */}
          {publishEnabled ? (
            <Badge variant="success">{t('shop.efl.official.badge')}</Badge>
          ) : null}
        </div>

        <section className="flex flex-col gap-1">
          <p className="text-sm leading-relaxed text-foreground/80">{efl ?? withContract}</p>
        </section>

        <section className="flex flex-col gap-1">
          <h4 className="text-sm font-semibold text-foreground">{t('shop.efl.tos.title')}</h4>
          <p className="text-sm text-foreground/70">{tos ?? withContract}</p>
        </section>

        <section className="flex flex-col gap-1">
          <h4 className="text-sm font-semibold text-foreground">{t('shop.efl.yrac.title')}</h4>
          <p className="text-sm text-foreground/70">{yrac ?? withContract}</p>
        </section>
      </CardBody>
    </Card>
  );
}
